/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import isNil from "lodash/isNil";
import { Column, ComparisonOperator, Sort, SortType, TableColumnParam } from "@evergis/ui";

import { useToggle } from "../../../hooks/useToggle";
import { useTableFilterApply } from "./useTableFilterApply";
import { useTableColumns } from "./useTableColumns";
import { FilterCondition } from "../../../evergis/core/condition/FilterCondition";
import { updateLayerMeta } from "../../../ducks/project/project";
import { getTableFilterConditions } from "../../../ducks/table/selectors";
import { getProjectLayers } from "../../../ducks/project/selectors";
import { updateTableFilterConditions } from "../../../ducks/table/table";
import { useTableSelection } from "../../../hooks/table/useTableSelection";
import { useTableSort } from "./useTableSort";
import { getDateFilterLayers } from "../../../ducks/dateFilter/selectors";
import { resetGeometrySelection } from "../../../ducks/geometrySelection/geometrySelection";
import { getHiddenLegends } from "../../../ducks/style/selectors";
import { useGeometrySelectionContext } from "../../../hooks/geometrySelection/useGeometrySelectionContext";
import { useSGisLayer } from "../../../hooks/sgis/useSGisLayers";
import { useSGisLayerRedraw } from "../../../hooks/sgis/useSGisLayerRedraw";
import { useTableLayerId } from "../../../hooks/table/useTableLayerId";

import { FilterConditionOperation, FilterConditionType } from "../../../components/LayerFilter/types";
import { SetTableData, TableQueryParams } from "../types";
import { LayerInfo } from "../../../interfaces/resourceInfo";

interface TablePropsResponse {
  processedLayer: React.MutableRefObject<string>;
  tableColumns: Column[];
  columnParams: TableColumnParam[];
  updateColumnParams: (state: TableColumnParam[]) => void;
  queryParams: TableQueryParams;
  sort: Record<string, Sort>;
  changeSort: SetTableData;
  handleToggleSort: (id: string, type: string) => void;
  currentPage: number;
  onPageChange: (state: number) => void;
  isFiltering: boolean;
  toggleFiltering: VoidFunction;
  applyFilterToLayer: VoidFunction;
  isFilterApplyingToLayer: boolean;
  clearFilters: VoidFunction;
  purgeTable: VoidFunction;
}

export const TABLE_PAGE_LIMIT = 200;
export const DEFAULT_COLUMN_PARAMS = [{ id: "EVENT_DATE", width: 250 }];

export const useTableProps = (
  layerInfoFull: LayerInfo,
  defaultColumnParams: TableColumnParam[] = DEFAULT_COLUMN_PARAMS,
): TablePropsResponse => {
  const dispatch = useDispatch();
  const [currentLayer, setTableLayerId] = useTableLayerId();
  const sgisLayer = useSGisLayer(currentLayer);
  const redrawLayer = useSGisLayerRedraw(sgisLayer);
  const [currentPage, onPageChange] = useState<number>(1);
  const [queryParams, setQueryParams] = useState<TableQueryParams>({ query: null, conditions: [] });
  const [columnParams, updateColumnParams] = useState<TableColumnParam[]>(defaultColumnParams);
  const [isFiltering, toggleFiltering] = useToggle();
  const [sort, changeSort] = useTableSort();
  const { filter: applyFilterToLayer, loading: isFilterApplyingToLayer } = useTableFilterApply(layerInfoFull);
  const { tableColumns, filters, setTableFilters } = useTableColumns(layerInfoFull);
  const currentProjectLayers = useSelector(getProjectLayers);
  const processedLayer = useRef<string>();
  const tableFilterConditions = useSelector(getTableFilterConditions);
  const [, setTableSelection] = useTableSelection();
  const dateFilterLayers = useSelector(getDateFilterLayers);
  const { purgeFeatures } = useGeometrySelectionContext();
  const hiddenLegends = useSelector(getHiddenLegends);

  const handleToggleSort = useCallback(
    (id: string, type: SortType) => {
      changeSort({
        ...sort,
        [layerInfoFull.name]: type !== undefined ? { [id]: type } : {},
      });
    },
    [changeSort, layerInfoFull.name, sort],
  );

  const handleToggleFiltering = useCallback(() => {
    setQueryParams({ query: null, conditions: [] });

    setTableFilters({
      ...filters,
      [layerInfoFull.name]: {
        ...filters?.[layerInfoFull.name],
        ...Object.keys(filters?.[layerInfoFull.name] || {})
          .filter(item => !dateFilterLayers?.[layerInfoFull.name]?.includes(item))
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: undefined,
            }),
            {},
          ),
      },
    });

    window.setTimeout(toggleFiltering, 0);
  }, [setTableFilters, filters, layerInfoFull.name, toggleFiltering, dateFilterLayers]);

  const handleApplyFilter = useCallback(() => {
    applyFilterToLayer().then(() => {
      purgeFeatures?.();
      redrawLayer();
      dispatch(resetGeometrySelection());
      handleToggleFiltering();
    });
  }, [applyFilterToLayer, dispatch, handleToggleFiltering, purgeFeatures, redrawLayer]);

  const applyFilterToMap = useCallback(
    (layerName: string, condition: string | null) => {
      if (!layerName || !currentProjectLayers[layerName]) {
        return;
      }

      dispatch(
        updateLayerMeta({
          name: layerName,
          condition,
        }),
      );
    },
    [currentProjectLayers, dispatch],
  );

  const clearFilters = useCallback(() => {
    setTableFilters({
      ...filters,
      [layerInfoFull.name]: undefined,
    });

    dispatch(
      updateLayerMeta({
        name: layerInfoFull.name,
        condition: hiddenLegends[layerInfoFull.name]?.join(` ${FilterConditionOperation.AND} `) || null,
      }),
    );
  }, [setTableFilters, filters, layerInfoFull.name, dispatch, hiddenLegends]);

  const purgeTable = useCallback(() => {
    Object.keys(currentProjectLayers).forEach((layerName: string) => {
      applyFilterToMap(layerName, null);
    });

    setTableFilters(undefined);
    changeSort(undefined);
    setTableSelection(undefined);
    setTableLayerId(undefined);
    dispatch(updateTableFilterConditions(undefined));
  }, [applyFilterToMap, currentProjectLayers, setTableFilters]);

  useEffect(() => {
    if (!isFiltering) {
      return;
    }

    const conditions: FilterConditionType[] = Object.entries(filters?.[layerInfoFull.name] ?? {}).reduce(
      (acc, [key, option]) => {
        if (!isNil(option?.value) || option?.isInactive === true) {
          return [
            ...acc,
            {
              attribute: key,
              comparisonOperator: option?.filterType.value,
              value: !option?.isInactive ? option?.value : null,
              type: option?.dataType,
            },
          ];
        }

        return acc;
      },
      [],
    );

    const checkboxesConditions = conditions.filter(
      item => item.comparisonOperator === ("SeveralValues" as ComparisonOperator),
    );

    const checkboxesQueries = checkboxesConditions.length
      ? checkboxesConditions
          .map(item =>
            (item.value as string[]).length
              ? `(${(item.value as string[])
                  .map(innerItem => `${item.attribute} == "${innerItem}"`)
                  .join(` ${FilterConditionOperation.OR} `)})`
              : "",
          )
          .join(` ${FilterConditionOperation.AND} `)
      : "";

    const updatedQuery = [
      FilterCondition.from(
        conditions.filter(item => item.comparisonOperator !== ("SeveralValues" as ComparisonOperator)),
        FilterConditionOperation.AND,
      ).toString(),
      checkboxesQueries,
    ]
      .filter(Boolean)
      .join(` ${FilterConditionOperation.AND} `);

    if (updatedQuery !== queryParams.query) {
      onPageChange(1);
      setQueryParams({ query: updatedQuery, conditions });

      dispatch(
        updateTableFilterConditions({
          ...tableFilterConditions,
          [layerInfoFull.name]: updatedQuery,
        }),
      );
    }
  }, [filters, queryParams, isFiltering, layerInfoFull]);

  useEffect(() => {
    if (processedLayer.current !== currentLayer) {
      onPageChange(1);
      updateColumnParams(defaultColumnParams);

      if (
        (filters?.[currentLayer] !== undefined && !isFiltering) ||
        (filters?.[currentLayer] === undefined && isFiltering)
      ) {
        toggleFiltering();
      }
    }

    processedLayer.current = currentLayer;
  }, [currentLayer, filters, isFiltering]);

  useEffect(() => {
    Object.keys(currentProjectLayers).forEach((layerName: string) => {
      const condition = [
        tableFilterConditions?.[layerName],
        hiddenLegends?.[layerName]?.join(` ${FilterConditionOperation.AND} `),
      ]
        .filter(Boolean)
        .join(` ${FilterConditionOperation.AND} `);

      applyFilterToMap(layerName, condition);
    });
  }, [tableFilterConditions, hiddenLegends]);

  useEffect(() => {
    setQueryParams({ query: null, conditions: [] });
  }, [layerInfoFull?.name]);

  return {
    processedLayer,
    tableColumns,
    columnParams,
    updateColumnParams,
    queryParams,
    sort,
    changeSort,
    handleToggleSort,
    currentPage,
    onPageChange,
    isFiltering,
    toggleFiltering: handleToggleFiltering,
    applyFilterToLayer: handleApplyFilter,
    isFilterApplyingToLayer,
    clearFilters,
    purgeTable,
  };
};
